import type { LinePaint } from 'mapbox-gl';

/**
 * How an LTD stick is painted on a map, in one place.
 *
 * Three surfaces draw the same object: the Type Curve / Inventory Workspace map, the Unit
 * Builder's map, and the Add Wellbore tearsheet's picker. They used to each carry their own
 * hex, which is how the picker ended up purple while the other two were teal. The values here
 * were lifted verbatim from the Unit Builder — it is the surface the others are matched to —
 * so adopting this module changed nothing about what those two already rendered.
 *
 * The comments below are the original reasoning for each value; they are the argument against
 * "tidying" the shade later.
 */

/**
 * An LTD stick that is not selected.
 *
 * Carbon teal-50. Teal-40 (#08bdba) was tried and rejected: it reads about 2.2:1 against the
 * map's white sections and washes out entirely over the type-curve area's tint, which is too
 * little for a 2px dashed line. The dash is what marks a line as an LTD rather than a surveyed
 * trajectory — keep it even if the colour is ever revisited.
 *
 * `line-width` is deliberately absent: it differs by surface. Spread one of the two widths
 * below in alongside it.
 */
export const LTD_SLOT_LINE_PAINT: LinePaint = {
  'line-color': '#009d9a',
  'line-opacity': 1,
  'line-dasharray': [4, 3],
};

/**
 * The same stick once it is selected: two steps down the ramp from the base, heavier, and on a
 * tighter dash. The light/dark relationship is the point — it is what lets a reader tell a
 * selected lateral from its neighbours without a legend.
 */
export const LTD_SLOT_SELECTED_LINE_PAINT: LinePaint = {
  'line-color': '#005d5d',
  'line-width': 4.5,
  'line-dasharray': [2, 1.5],
};

/**
 * Heavier, for a surface where the laterals are the thing being edited — the Unit Builder and
 * the Add Wellbore picker.
 */
export const LTD_SLOT_WIDTH_EDITING = 2.5;

/** Lighter, for a map that merely shows them — the Type Curve / Inventory Workspace map. */
export const LTD_SLOT_WIDTH_VIEWING = 2;
